import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { Play, Pause, SkipForward, Square, Search, Music2, Disc3, Radio } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function MusicPanel() {
  const { active } = useServer();
  const [state, setState] = useState({ now: null, queue: [], is_playing: false, is_paused: false });
  const [voiceChannels, setVoiceChannels] = useState([]);
  const [vcId, setVcId] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const guildId = active?.id;

  const refresh = () => {
    if (!guildId || !active.bot_present) return;
    api.get(`/bot/music/queue?guild_id=${guildId}`).then((r) => setState(r.data)).catch(() => {});
    api.get(`/bot/music/voice-channels?guild_id=${guildId}`).then((r) => {
      setVoiceChannels(r.data);
      if (!vcId && r.data[0]) setVcId(r.data[0].id);
    }).catch(() => {});
  };

  useEffect(() => { refresh(); }, [guildId, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const action = async (a) => {
    setBusy(true);
    try {
      await api.post(`/bot/music/${a}?guild_id=${guildId}`);
      toast.success(`Music ${a}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const play = async (e) => {
    e.preventDefault();
    if (!query.trim() || !vcId) return;
    setBusy(true);
    try {
      await api.post(`/bot/music/play?guild_id=${guildId}`, { query, voice_channel_id: vcId });
      toast.success("Track queued");
      setQuery("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Play failed");
    } finally {
      setBusy(false);
    }
  };

  const status = state.is_playing ? "PLAYING" : state.is_paused ? "PAUSED" : "IDLE";

  return (
    <div data-testid="music-panel" className="space-y-8">
      <div>
        <div className="overline mb-2">// AUDIO ENGINE</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Music</h1>
        <p className="text-zinc-400 mt-2">Queue songs and the bot will play them in your voice channel.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 tactical-card p-6">
          <div className="flex items-center gap-5">
            <div className={`w-24 h-24 bg-brand-orange/10 border border-brand-orange flex items-center justify-center rounded-2xl`}>
              <Disc3 className={`w-10 h-10 text-brand-orange ${state.is_playing ? "animate-spin" : ""}`} style={{ animationDuration: "4s" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline">{status}</div>
              <div className="font-heading text-2xl font-bold uppercase tracking-tight truncate mt-1" data-testid="music-now-title">
                {state.now?.title || "— No track playing —"}
              </div>
              <div className="text-sm text-zinc-400 truncate">
                {state.now?.requested_by ? `Requested by ${state.now.requested_by}` : "Search a song below to start"}
              </div>
              {state.voice_channel && (
                <div className="font-mono-tabular text-xs text-zinc-500 mt-2 flex items-center gap-2">
                  <Radio className="w-3 h-3" /> Connected
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {state.is_paused ? (
              <button onClick={() => action("resume")} disabled={busy} className="btn-primary" data-testid="music-play-btn">
                <Play className="w-4 h-4" /> Resume
              </button>
            ) : (
              <button onClick={() => action("pause")} disabled={busy || !state.is_playing} className="btn-secondary" data-testid="music-pause-btn">
                <Pause className="w-4 h-4" /> Pause
              </button>
            )}
            <button onClick={() => action("skip")} disabled={busy || !state.is_playing} className="btn-secondary" data-testid="music-skip-btn">
              <SkipForward className="w-4 h-4" /> Skip
            </button>
            <button onClick={() => action("stop")} disabled={busy} className="btn-ghost border border-brand-line" data-testid="music-stop-btn">
              <Square className="w-4 h-4" /> Stop
            </button>
          </div>
        </div>

        <form onSubmit={play} className="tactical-card p-6 flex flex-col" data-testid="music-add-form">
          <div className="overline mb-1">// PLAY A SONG</div>
          <h2 className="font-heading text-xl font-bold uppercase mb-4">Search or paste URL</h2>

          <label className="overline block mb-1.5">Voice channel</label>
          <select className="tactical-input mb-3" value={vcId} onChange={(e) => setVcId(e.target.value)} data-testid="music-vc-select">
            {voiceChannels.length === 0 && <option value="">No voice channels</option>}
            {voiceChannels.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.members})</option>
            ))}
          </select>

          <label className="overline block mb-1.5">Song / URL</label>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              className="tactical-input pl-10"
              placeholder="e.g. Lofi beats, or YouTube link"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="music-query-input"
            />
          </div>
          <button type="submit" disabled={busy || !query.trim() || !vcId} className="btn-primary justify-center mt-auto" data-testid="music-play-submit">
            <Play className="w-4 h-4" /> Play
          </button>
        </form>
      </div>

      <div className="tactical-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline">// UP NEXT</div>
            <h2 className="font-heading text-xl font-bold uppercase">Queue ({state.queue?.length || 0})</h2>
          </div>
        </div>
        <div className="space-y-2">
          {(!state.queue || state.queue.length === 0) && (
            <div className="text-center py-12 text-zinc-500 font-mono-tabular text-sm">
              QUEUE IS EMPTY
            </div>
          )}
          {(state.queue || []).map((t, i) => (
            <div key={t.url || i} className="border border-brand-line p-3 flex items-center gap-4 hover:border-brand-orange transition-colors rounded-lg" data-testid={`queue-track-${i}`}>
              <div className="font-mono-tabular text-xs text-zinc-500 w-8">{String(i + 1).padStart(2, "0")}</div>
              <Music2 className="w-4 h-4 text-brand-orange shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{t.title}</div>
                <div className="text-xs text-zinc-500 truncate">req. {t.requested_by || "—"}</div>
              </div>
              {t.duration && <div className="font-mono-tabular text-xs text-zinc-500 hidden sm:block">{Math.floor(t.duration/60)}:{String(t.duration%60).padStart(2,"0")}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
